// Chat console — clean "LLM client" aesthetic: centered column, user
// bubbles, plain assistant prose, quiet tool-activity rows, rounded composer.
import { memo, useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { formatCost, formatTokens } from "@/lib/pricing";
import { ReferenceBar, type ReferenceAsset } from "./ReferenceBar";
import { useAvailableModelsQuery, useModelPrefsQuery } from "@/api/settings";
import { sortModels } from "@/lib/modelSort";
import { ModelCombobox } from "@/components/ModelCombobox";
import type { Message, MessagePart } from "@/hooks/useClaudeSession";

/**
 * Renders assistant message bodies as markdown (headings, bold, lists,
 * code, tables). Sans-serif prose; monospace is reserved for code.
 */
function MarkdownBody({ text }: { text: string }) {
  return (
    <div
      className={[
        "text-[15px] leading-7 text-ink",
        "[&_h1]:mt-4 [&_h1]:mb-2 [&_h1]:text-xl [&_h1]:font-semibold",
        "[&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-lg [&_h2]:font-semibold",
        "[&_h3]:mt-3 [&_h3]:mb-1.5 [&_h3]:text-base [&_h3]:font-semibold",
        "[&_h4]:mt-3 [&_h4]:mb-1 [&_h4]:text-[15px] [&_h4]:font-semibold",
        "[&_p]:my-2 [&_p]:whitespace-pre-wrap",
        "[&_strong]:font-semibold",
        "[&_em]:italic",
        "[&_a]:text-accent [&_a]:underline [&_a]:underline-offset-2",
        "[&_ul]:my-2 [&_ul]:ml-5 [&_ul]:list-disc [&_ul]:space-y-1",
        "[&_ol]:my-2 [&_ol]:ml-5 [&_ol]:list-decimal [&_ol]:space-y-1",
        "[&_code]:rounded [&_code]:bg-code [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[13px]",
        "[&_pre]:my-3 [&_pre]:overflow-x-auto [&_pre]:rounded-xl [&_pre]:border [&_pre]:border-line [&_pre]:bg-code [&_pre]:p-3",
        "[&_pre>code]:bg-transparent [&_pre>code]:p-0 [&_pre>code]:text-[13px]",
        "[&_blockquote]:my-3 [&_blockquote]:border-l-2 [&_blockquote]:border-line-strong [&_blockquote]:pl-3 [&_blockquote]:text-ink-2",
        "[&_hr]:my-4 [&_hr]:border-line",
        "[&_table]:my-3 [&_table]:border-collapse [&_table]:text-sm",
        "[&_th]:border [&_th]:border-line [&_th]:px-2.5 [&_th]:py-1.5 [&_th]:text-left [&_th]:font-semibold",
        "[&_td]:border [&_td]:border-line [&_td]:px-2.5 [&_td]:py-1.5",
      ].join(" ")}
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{text}</ReactMarkdown>
    </div>
  );
}

/** Map raw hook event subtypes to friendly labels. Anything unknown
 *  falls through to "Working…" — better than showing a raw identifier. */
function lifecycleLabel(phase: string | null): string {
  if (!phase) return "Thinking…";
  switch (phase) {
    case "hook_user_prompt_submit":
      return "Reading your prompt…";
    case "hook_pre_tool_use":
      return "Calling a tool…";
    case "hook_post_tool_use":
      return "Processing tool result…";
    case "hook_session_start":
    case "hook_session_end":
      return "Setting up…";
    case "hook_session_console_compact":
    case "hook_compact":
      return "Compacting context…";
    case "hook_stop":
      return "Wrapping up…";
    case "hook_format_reminder":
    case "hook_format":
      return "Formatting output…";
    default:
      return "Working…";
  }
}

/** Verb for the status line. Stream-derived activity (what the model is
 *  literally emitting right now) beats the coarser lifecycle hook phase. */
function activityLabel(activity: string | null, phase: string | null): string {
  if (activity && activity.startsWith("tool:")) {
    return `Running ${activity.slice(5)}…`;
  }
  if (activity === "thinking") return "Thinking…";
  if (activity === "writing") return "Writing…";
  return lifecycleLabel(phase);
}

function Spinner() {
  return (
    <svg
      className="h-3.5 w-3.5 animate-spin text-ink-3"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="3"
      />
      <path
        className="opacity-75"
        d="M12 2a10 10 0 0 1 10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Status line above the composer: `⟳ Running Bash… · ~3.4k tok · 42s`.
 *  Elapsed is computed from the manager's generationStartedAt so it
 *  survives tab switches and remounts instead of resetting to 0s. */
function GenerationStatusBar({
  phase,
  activity,
  startedAt,
  liveTokens,
}: {
  phase: string | null;
  activity: string | null;
  startedAt: number | null;
  liveTokens: number;
}) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsed = startedAt
    ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
    : 0;
  return (
    <div className="mx-auto flex w-full max-w-3xl shrink-0 items-center gap-2 px-4 py-1.5 text-[13px] text-ink-3">
      <Spinner />
      <span className="text-ink-2">{activityLabel(activity, phase)}</span>
      <span className="ml-auto flex items-center gap-3">
        {liveTokens > 0 && <span>~{formatTokens(liveTokens)} tokens</span>}
        <span>{elapsed}s</span>
      </span>
    </div>
  );
}

// ── Message part renderers ──────────────────────────────────────────────────
// Each part type is its own memoized component. During streaming only the
// part actually receiving tokens changes identity, so everything else skips
// re-rendering — the transcript stays cheap no matter how long it grows.

const ThinkingPart = memo(function ThinkingPart({
  text,
  live,
}: {
  text: string;
  live: boolean;
}) {
  if (live) {
    // While streaming, show the reasoning inline (dim) so there's always
    // visible motion; it collapses into a disclosure once the turn is done.
    return (
      <div className="my-2 border-l-2 border-line pl-3">
        <div className="mb-1 text-[13px] font-medium text-ink-3">Thinking…</div>
        <div className="whitespace-pre-wrap text-sm italic leading-6 text-ink-3">
          {text}
        </div>
      </div>
    );
  }
  return (
    <details className="my-2 group">
      <summary className="cursor-pointer list-none text-[13px] font-medium text-ink-3 transition-colors hover:text-ink-2">
        <span className="mr-1 inline-block transition-transform group-open:rotate-90">
          ›
        </span>
        Thought process
      </summary>
      <div className="mt-1 whitespace-pre-wrap border-l-2 border-line pl-3 text-sm italic leading-6 text-ink-3">
        {text}
      </div>
    </details>
  );
});

const TextPart = memo(function TextPart({ text }: { text: string }) {
  return <MarkdownBody text={text} />;
});

const ToolPartView = memo(function ToolPartView({
  part,
}: {
  part: Extract<MessagePart, { kind: "tool" }>;
}) {
  const dot = part.resultError ? (
    <span className="h-2 w-2 rounded-full bg-danger" />
  ) : part.done ? (
    <span className="h-2 w-2 rounded-full bg-success" />
  ) : (
    <span className="h-2 w-2 animate-pulse rounded-full bg-accent" />
  );
  return (
    <div className="my-1.5 overflow-hidden rounded-lg border border-line bg-surface-2">
      <div className="flex min-w-0 items-center gap-2 px-3 py-1.5 text-[13px]">
        {dot}
        <span className="shrink-0 font-medium text-ink-2">{part.name}</span>
        {part.summary && (
          <span className="truncate font-mono text-xs text-ink-3">
            {part.summary}
          </span>
        )}
      </div>
      {part.resultPreview && (
        <pre
          className={`whitespace-pre-wrap break-words border-t border-line px-3 py-1.5 font-mono text-xs leading-5 ${
            part.resultError ? "text-danger" : "text-ink-3"
          }`}
        >
          {part.resultPreview}
        </pre>
      )}
    </div>
  );
});

/** Structured assistant body. Falls back to the flat `content` string for
 *  messages restored from pre-parts caches or synthesized from DB rows. */
function AssistantBody({ msg }: { msg: Message }) {
  const parts = msg.parts;
  if (!parts || parts.length === 0) {
    return (
      <MarkdownBody
        text={msg.content || (msg.status === "streaming" ? "…" : "")}
      />
    );
  }
  const lastIdx = parts.length - 1;
  return (
    <>
      {parts.map((p, i) =>
        p.kind === "thinking" ? (
          <ThinkingPart
            key={i}
            text={p.text}
            live={msg.status === "streaming" && i === lastIdx}
          />
        ) : p.kind === "text" ? (
          <TextPart key={i} text={p.text} />
        ) : (
          <ToolPartView key={`${p.toolId}-${i}`} part={p} />
        ),
      )}
    </>
  );
}

/**
 * Render a single chat turn. User messages are right-aligned bubbles;
 * assistant replies are plain full-width prose (LLM-client convention).
 */
const MessageBlock = memo(function MessageBlock({ msg }: { msg: Message }) {
  if (msg.role === "user") {
    return (
      <div className="mb-5 flex justify-end">
        <div className="max-w-[80%] whitespace-pre-wrap break-words rounded-2xl bg-surface-3 px-4 py-2.5 text-[15px] leading-6 text-ink">
          {msg.content}
        </div>
      </div>
    );
  }

  if (msg.role === "system") {
    return (
      <div className="mb-5 rounded-xl border border-line bg-surface-2 px-4 py-3">
        <div className="mb-1 text-xs font-medium text-ink-3">
          Restored session
        </div>
        <pre className="whitespace-pre-wrap break-words font-mono text-xs leading-5 text-ink-3">
          {msg.content}
        </pre>
      </div>
    );
  }

  // Assistant
  return (
    <div className="mb-6">
      <AssistantBody msg={msg} />
      {msg.status === "streaming" && (
        <span className="ml-0.5 inline-block h-4 w-2 animate-pulse rounded-sm bg-ink-3 align-text-bottom" />
      )}
      {msg.status === "cancelled" && (
        <div className="mt-2 text-[13px] italic text-ink-3">
          Generation stopped.
        </div>
      )}
      {msg.status === "error" && msg.errorMessage && (
        <div className="mt-2 rounded-lg border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger">
          {msg.errorMessage}
        </div>
      )}
    </div>
  );
});

/** Compact model picker in the composer footer. "Auto" follows the user's
 *  saved per-kind preference (Settings → Models) / server default; explicit
 *  picks ride each prompt and switch the session model mid-flight. */
const PROVIDER_LABELS: Record<string, string> = {
  claude: "Anthropic",
  openai: "OpenAI",
  opencode: "OpenCode",
  gemini: "Google",
};

function ComposerModelSelect({
  value,
  onChange,
  provider,
  onProviderChange,
  isSvg,
}: {
  value: string;
  onChange: (v: string) => void;
  provider: string;
  onProviderChange?: (p: string) => void;
  isSvg?: boolean;
}) {
  const { data } = useAvailableModelsQuery();
  const { data: prefs } = useModelPrefsQuery();
  // The server only offers providers this user can authenticate with — if
  // the current selection isn't among them (e.g. default "claude" with no
  // Anthropic credentials connected), hop to the first real option.
  useEffect(() => {
    if (!data || !onProviderChange) return;
    const avail = data.providers ?? [];
    if (avail.length > 0 && !avail.includes(provider)) {
      onProviderChange(avail[0]!);
      onChange(""); // model choices don't carry across providers
    }
  }, [data, provider, onProviderChange, onChange]);
  // What Auto actually resolves to: composer choice > saved per-kind pref >
  // server default (Claude) / the account's own default (other providers).
  // What Auto will actually run — shown as a caption under the picker.
  const autoResolved = (() => {
    if (provider === "openai") return data?.openai_default ?? "account default";
    if (provider === "opencode") return data?.opencode_default ?? "account default";
    const pref = isSvg ? prefs?.svg_model : prefs?.remotion_model;
    let resolved = pref || data?.claude_default;
    // Bare aliases (opus/sonnet/haiku/fable) resolve to the newest concrete
    // model of that family from the live list.
    if (resolved && !/\d/.test(resolved)) {
      const alias = resolved;
      const claudeModels = sortModels(
        (data?.text ?? []).filter((m) => m.provider === "claude" && !/^(opus|sonnet|haiku|fable)$/.test(m.id)),
      );
      const concrete = claudeModels.find((m) => m.id.includes(alias));
      if (concrete) resolved = concrete.id;
    }
    return resolved ?? null;
  })();
  const providers = data?.providers ?? ["claude"];
  // Only this provider's models populate the model select.
  const models = (data?.text ?? []).filter((m) => m.provider === provider);
  return (
    <div className="flex min-w-0 shrink-0 flex-col gap-0.5">
    {/* Always occupies its line so selecting/deselecting Auto never
        shifts the picker row — only the text appears and disappears. */}
    <span
      className="min-h-[14px] w-[11rem] self-end truncate pl-1 text-[10px] leading-[14px] text-ink-3"
      title={value || autoResolved || undefined}
    >
      {value || autoResolved || "\u00A0"}
    </span>
    <label className="flex shrink-0 items-center gap-1">
      {onProviderChange && providers.length > 1 && (
        <select
          value={provider}
          onChange={(e) => {
            onProviderChange(e.target.value);
            onChange(""); // model choices don't carry across providers
          }}
          className="w-[6.5rem] cursor-pointer truncate rounded-md border border-line bg-surface-2 px-1.5 py-0.5 text-xs text-ink-2 focus:border-line-strong focus:outline-none"
          aria-label="Provider"
        >
          {providers.map((p) => (
            <option key={p} value={p}>
              {PROVIDER_LABELS[p] ?? p}
            </option>
          ))}
        </select>
      )}
      <span className="hidden sm:inline">Model</span>
      <ModelCombobox value={value} onChange={onChange} models={models} size="sm" autoHint={autoResolved} />
    </label>
    </div>
  );
}

interface ConversationHistoryProps {
  /**
   * Structured chat transcript for the current session. Each user prompt
   * produces a user message + an assistant reply that grows in place as
   * tokens stream in. Preserved across turns within a session; cleared on
   * explicit "New Session".
   */
  messages?: Message[];
  /** True while a generation is actively streaming. */
  isGenerating?: boolean;
  /** WebSocket connection state. */
  isConnected?: boolean;
  /** Generic error banner text. */
  error?: string | null;
  /**
   * True when the messages shown were loaded from localStorage, not a live
   * session. The backend subprocess may have been killed while the user was
   * away — the next prompt will respawn it with `--resume` and pay a reload
   * cost. We show a warning banner so the user isn't surprised by the latency.
   */
  transcriptRestoredFromCache?: boolean;
  /** True while the Resume Session button is loading from the backend. */
  isLoadingJsonl?: boolean;
  /** Called when the user clicks Resume Session — loads the JSONL from disk. */
  onLoadJsonl?: () => void;
  /** Submit a new prompt. */
  onSubmitPrompt: (text: string) => void;
  /**
   * Optional pre-filled prompt body. Used by the "Upgrade Text Fields" flow
   * (TemplateDetail → /create) to seed the input with a canned migration
   * prompt the user can review and send. Applied once on mount.
   */
  initialDraft?: string;
  /** Composer model choice ("" = saved prefs / server default). When
   *  provided together with onModelChange, a compact picker renders in the
   *  composer footer, fed by the user's actually-available models. */
  model?: string;
  onModelChange?: (model: string) => void;
  /** Composer provider choice; with onProviderChange renders the provider
   *  select ahead of the model select. */
  genProvider?: string;
  onProviderChange?: (provider: string) => void;
  /** SVG session? Drives which saved pref Auto resolves to. */
  isSvgSession?: boolean;
  /**
   * Last out-of-band lifecycle phase from the backend (hook events).
   * Drives a single-line status indicator above the prompt input —
   * NOT shown in the chat transcript. null while idle.
   */
  currentLifecycle?: string | null;
  /** Stream-derived activity ("thinking" | "writing" | "tool:<Name>"). */
  currentActivity?: string | null;
  /** ms epoch when the in-flight generation started; null while idle. */
  generationStartedAt?: number | null;
  /** Live output-token estimate for the in-flight generation. */
  liveOutputTokens?: number;
  /** Send a correction to Claude mid-generation without stopping. */
  onInterrupt: (text: string) => void;
  /** Cancel the in-flight generation. Shown as a Stop button while isGenerating. */
  onCancel: () => void;
  /**
   * If true, the prompt input reflects "you're iterating on an existing
   * result" (placeholder copy changes). This is purely cosmetic — the
   * backend figures out whether to treat each prompt as new or iteration
   * based on its own session state.
   */
  isIterating?: boolean;
  /** Placeholder text for the prompt input. */
  inputPlaceholder?: string;
  /**
   * Total input tokens across every completed prompt in the current session
   * chain. Summed client-side from the Prompt rows returned by
   * useSessionChainQuery. Excludes the live turn still streaming.
   */
  sessionInputTokens?: number;
  /** Total output tokens across the current session chain. */
  sessionOutputTokens?: number;
  /**
   * Upper-bound USD cost estimate for the current session. Upper bound
   * because prompt caching discounts aren't reflected — the real bill from
   * Anthropic is usually lower.
   */
  sessionCostUsd?: number;
  /** Reference images attached to the next generation (shared with the
   *  workspace Prompt popover — same underlying state). */
  referenceAssets?: ReferenceAsset[];
  /** True while a reference image upload is in flight. */
  referenceUploading?: boolean;
  /** Inline error for a rejected reference upload. */
  referenceError?: string | null;
  /** Max number of reference images that can be attached. */
  maxReferences?: number;
  /** Upload + attach the selected image files. */
  onAttachReferences?: (files: FileList | null) => void;
  /** Drop an attached reference chip (local only; server TTL cleans up). */
  onRemoveReference?: (id: string) => void;
}

/**
 * The Build tab: a centered chat column with the transcript and a rounded
 * composer pinned at the bottom.
 */
export function ConversationHistory({
  messages,
  isGenerating,
  isConnected,
  error,
  onSubmitPrompt,
  onInterrupt,
  onCancel,
  isIterating,
  inputPlaceholder,
  transcriptRestoredFromCache: _transcriptRestoredFromCache,
  isLoadingJsonl,
  onLoadJsonl: _onLoadJsonl,
  sessionInputTokens = 0,
  sessionOutputTokens = 0,
  sessionCostUsd = 0,
  initialDraft,
  model,
  onModelChange,
  genProvider,
  onProviderChange,
  isSvgSession,
  currentLifecycle,
  currentActivity,
  generationStartedAt,
  liveOutputTokens = 0,
  referenceAssets = [],
  referenceUploading = false,
  referenceError = null,
  maxReferences = 6,
  onAttachReferences,
  onRemoveReference,
}: ConversationHistoryProps) {
  const totalTokens = sessionInputTokens + sessionOutputTokens;
  const hasUsage = totalTokens > 0;
  const [draft, setDraft] = useState(initialDraft ?? "");

  // Sync initialDraft into state when the parent supplies it asynchronously
  // (sessionStorage handoff from TemplateDetail's Upgrade Text Fields button
  // sets it via setInitialDraft after the first render). useState's initial
  // value only fires once, so we copy the prop in via effect — but only
  // when the user hasn't started typing, so we don't clobber their work.
  useEffect(() => {
    if (initialDraft && !draft) {
      setDraft(initialDraft);
    }
  }, [initialDraft]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = useCallback(() => {
    const text = draft.trim();
    if (!text) return;
    if (isGenerating) {
      // Mid-generation: send as an interrupt/correction
      onInterrupt(text);
    } else {
      onSubmitPrompt(text);
    }
    setDraft("");
  }, [draft, isGenerating, onSubmitPrompt, onInterrupt]);

  const safeMessages = messages ?? [];
  const hasMessages = safeMessages.length > 0;

  // Sticky-bottom scroll. Pinned-ness is tracked from the user's own scroll
  // events (measured BEFORE content grows) — checking distance-from-bottom
  // after appending, like we used to, unpins the moment a flush is taller
  // than the threshold, which is why the transcript stopped following.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const pinnedRef = useRef(true);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);

  const handleTranscriptScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    // Programmatic scrolls land at ~0 and simply re-confirm pinned=true.
    const pinned = distFromBottom < 60;
    pinnedRef.current = pinned;
    setShowJumpToLatest(!pinned);
  }, []);

  const jumpToLatest = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    pinnedRef.current = true;
    setShowJumpToLatest(false);
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, []);

  // After every render (streamed tokens, tool cards, new messages), follow
  // the bottom while pinned. Cheap guard, no deps — content identity churn
  // during streaming makes a dependency list unreliable.
  useEffect(() => {
    const el = scrollRef.current;
    if (el && pinnedRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  });
  const isEmpty = !hasMessages;

  const effectivePlaceholder =
    inputPlaceholder ??
    (isGenerating
      ? "Send a correction to steer the current generation…"
      : isIterating
        ? "Describe what to change…"
        : "Describe your motion graphics template…");

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Connection status + error banner */}
      {(isConnected === false || error) && (
        <div className="mx-auto flex w-full max-w-3xl items-center gap-2 px-4 pt-2">
          {isConnected === false && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-danger/30 bg-danger-soft px-2.5 py-0.5 text-xs text-danger">
              <span className="h-1.5 w-1.5 rounded-full bg-danger" />
              Disconnected
            </span>
          )}
          {error && (
            <span className="rounded-lg border border-danger/30 bg-danger-soft px-3 py-1 text-sm text-danger">
              {error}
            </span>
          )}
        </div>
      )}

      {/* Empty state — a centered invitation, LLM-client style. */}
      {isEmpty && (
        <div className="flex flex-1 items-center justify-center px-4">
          <div className="text-center">
            <div className="mb-2 text-2xl font-semibold text-ink">
              {isLoadingJsonl ? "Loading previous session…" : "What are we making today?"}
            </div>
            <div className="text-[15px] text-ink-3">
              {isLoadingJsonl
                ? "Reading the session log from disk — no tokens consumed."
                : "Describe a motion graphic and MotionForge will build it live."}
            </div>
          </div>
        </div>
      )}

      {/* Transcript — centered chat column, sticky-bottom auto-scroll:
          follows new content while pinned to the bottom; scrolling up
          unpins; returning to the bottom (or the ↓ Latest chip) re-pins. */}
      {hasMessages && (
        <div className="relative min-h-0 flex-1">
          <div
            ref={scrollRef}
            onScroll={handleTranscriptScroll}
            className="h-full overflow-y-auto"
          >
            <div className="mx-auto w-full max-w-3xl px-4 py-6">
              {safeMessages.map((m) => (
                <MessageBlock key={m.id} msg={m} />
              ))}
            </div>
          </div>
          {showJumpToLatest && (
            <button
              type="button"
              onClick={jumpToLatest}
              className="absolute bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-full border border-line bg-surface px-3 py-1 text-xs font-medium text-ink-2 shadow-md transition-colors hover:bg-surface-3 hover:text-ink"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7-7-7M12 21V3" />
              </svg>
              Latest
            </button>
          )}
        </div>
      )}

      {/* Generation status — slim line above the composer. */}
      {isGenerating && (
        <GenerationStatusBar
          phase={currentLifecycle ?? null}
          activity={currentActivity ?? null}
          startedAt={generationStartedAt ?? null}
          liveTokens={liveOutputTokens}
        />
      )}

      {/* Composer — rounded card pinned at the bottom of the column. */}
      <div className="mx-auto w-full max-w-3xl shrink-0 px-4 pb-4 pt-2">
        <div className="rounded-3xl border border-line bg-surface shadow-sm transition-colors focus-within:border-line-strong">
          {onAttachReferences && (
            <div className="px-4 pt-3">
              <ReferenceBar
                references={referenceAssets}
                uploading={referenceUploading}
                error={referenceError}
                max={maxReferences}
                onAttach={onAttachReferences}
                onRemove={onRemoveReference ?? (() => {})}
              />
            </div>
          )}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={effectivePlaceholder}
            rows={2}
            className="w-full resize-none bg-transparent px-4 pt-3 text-[15px] leading-6 text-ink placeholder:text-ink-3 focus:outline-none"
          />
          <div className="flex items-center justify-between gap-2 px-3 pb-2.5">
            <div className="flex min-w-0 items-center gap-3 pl-1 text-xs text-ink-3">
              {onModelChange && (
                <ComposerModelSelect
                  value={model ?? ""}
                  onChange={onModelChange}
                  provider={genProvider ?? "claude"}
                  onProviderChange={onProviderChange}
                  isSvg={isSvgSession}
                />
              )}
              {hasUsage && (
                <span
                  title={`${sessionInputTokens.toLocaleString()} in · ${sessionOutputTokens.toLocaleString()} out — upper-bound estimate (prompt caching not reflected)`}
                >
                  {formatTokens(totalTokens)} tokens · ~{formatCost(sessionCostUsd)}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isGenerating && (
                <button
                  type="button"
                  onClick={onCancel}
                  className="rounded-full border border-line px-3.5 py-1.5 text-sm font-medium text-danger transition-colors hover:bg-danger-soft"
                >
                  Stop
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!draft.trim()}
                aria-label={isGenerating ? "Send correction" : "Send"}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-30"
              >
                <svg
                  className="h-4 w-4"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 19V5m0 0l-6 6m6-6l6 6"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div className="mt-1.5 text-center text-xs text-ink-3">
          {isGenerating
            ? "Generating — type to send a correction, Enter to send"
            : "Enter to send · Shift+Enter for a new line"}
        </div>
      </div>
    </div>
  );
}
